<script setup>
import { computed, ref, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useNotification } from '@kyvg/vue3-notification'
import { track, EVENTS } from '@/lib/tracking'
import ProjectService from '@/services/ProjectService'
import ModalBox from '@/components/modals/ModalBox.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import Alert from '@/components/Atoms/Alert.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconChevronLeft from '@/components/icon/IconChevronLeft.vue'
import IconPlus from '@/components/icon/IconPlus.vue'
import ProjectTile from './ProjectTile.vue'
import ProjectForm from './ProjectForm.vue'

/**
 * Two views in one modal: the list of the account's workspaces, and an editor
 * for one of them (rename, recolor, delete). Deleting is the only truly
 * destructive action in the whole feature, so it sits behind an explicit
 * confirmation that requires typing the workspace's name.
 */
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue', 'create', 'switch'])

const store = useStore()
const { notify } = useNotification()

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const projects = computed(() => store.getters['Projects/projects'])
const activeProject = computed(() => store.getters['Projects/activeProject'])
const allowance = computed(() => store.getters['Projects/projectAllowance'])
const canCreate = computed(() => store.getters['Projects/canCreateProject'])

const allowanceLabel = computed(() => {
  const limit = Number(allowance.value.limit) || 0
  if (!limit) return `${projects.value.length} ${projects.value.length === 1 ? 'workspace' : 'workspaces'}`
  const plan = allowance.value.planName ? ` on ${allowance.value.planName}` : ''
  return `${allowance.value.used} of ${limit} workspaces${plan}`
})

// --- what each workspace holds ----------------------------------------------------
// Fetched when the modal opens so the owner sees what a workspace contains
// before renaming or deleting it. Missing usage degrades to no meta line —
// the list must never wait on it.
const usageById = ref({})

const loadUsage = async () => {
  try {
    const usage = await ProjectService.getProjectUsage()
    const map = {}
    for (const entry of usage?.workspaces || []) map[entry.id] = entry
    usageById.value = map
  } catch (error) {
    usageById.value = {}
  }
}

const formatGb = (bytes) => {
  const amount = (bytes || 0) / 1000 / 1000 / 1000
  if (!amount) return '0 GB'
  return `${amount < 10 ? amount.toFixed(2).replace(/\.?0+$/, '') : Math.round(amount)} GB`
}

/** ["6 streams", "120 GB of storage"], [] when empty, or null when unknown. */
const contentParts = (project) => {
  const usage = usageById.value[project?.id]
  if (!usage) return null
  const streams = Number(usage.streams) || 0
  const storage = Number(usage.storageBytes) || 0
  const parts = []
  if (streams) parts.push(`${streams} ${streams === 1 ? 'stream' : 'streams'}`)
  if (storage) parts.push(`${formatGb(storage)} of storage`)
  return parts
}

/** Row meta: "6 streams · 120 GB of storage", "Empty", or '' when unknown. */
const contentsOf = (project) => {
  const parts = contentParts(project)
  if (!parts) return ''
  return parts.length ? parts.join(' · ') : 'Empty'
}

// --- editor state -------------------------------------------------------------
const editing = ref(null) // the workspace being edited, or null for the list view
const form = ref(null)
const draft = ref({ name: '', color: '' })
const saving = ref(false)
const saveError = ref('')

const isCurrent = (project) => project?.id === activeProject.value?.id
const isLast = computed(() => projects.value.length <= 1)

const isDirty = computed(() => (
  !!editing.value && (
    draft.value.name.trim() !== (editing.value.name || '') ||
    draft.value.color !== (editing.value.color || '')
  )
))
const canSave = computed(() => !!draft.value.name.trim() && !!draft.value.color && isDirty.value && !saving.value)

const openEditor = async (project) => {
  editing.value = project
  draft.value = { name: project.name || '', color: project.color || '' }
  saveError.value = ''
  resetDeleteState()
  await nextTick()
  form.value?.focus()
}

const backToList = () => {
  editing.value = null
  saveError.value = ''
  resetDeleteState()
}

const save = async () => {
  if (!canSave.value) return
  saving.value = true
  saveError.value = ''
  try {
    await store.dispatch('Projects/updateProject', {
      id: editing.value.id,
      name: draft.value.name.trim(),
      color: draft.value.color,
    })
    track(EVENTS.PROJECT_UPDATED, {
      renamed: draft.value.name.trim() !== editing.value.name,
      recolored: draft.value.color !== editing.value.color,
    })
    notify({ group: 'success', text: `${draft.value.name.trim()} was updated.` })
    backToList()
  } catch (error) {
    saveError.value = error?.message || 'Could not save the workspace. Please try again.'
  } finally {
    saving.value = false
  }
}

// --- delete ----------------------------------------------------------------------
const confirmingDelete = ref(false)
const deleteConfirmation = ref('')
const deleting = ref(false)
const deleteError = ref('')
// `[{ key, count, label }]` from a 409: what still lives in the workspace.
const deleteBlocking = ref([])

const resetDeleteState = () => {
  confirmingDelete.value = false
  deleteConfirmation.value = ''
  deleteError.value = ''
  deleteBlocking.value = []
}

// The server refuses all three of these with a 409; saying so up front, with
// the delete control disabled, beats letting the owner type the name out and
// then be told no.
const deleteBlockedReason = computed(() => {
  if (!editing.value) return ''
  if (editing.value.isDefault) return "This is your default workspace. It holds the account's original content and can't be deleted."
  if (isLast.value) return 'An account needs at least one workspace.'
  if (isCurrent(editing.value)) return "You're working in this workspace right now. Switch to another workspace first."
  return ''
})

/**
 * What the workspace still holds, phrased for the delete warning. The server
 * refuses to delete a workspace with content in it, so this is the list of what
 * has to be moved or removed first.
 */
const contentsWarning = computed(() => {
  if (!editing.value) return ''
  const parts = contentParts(editing.value)
  if (!parts) return ''
  if (!parts.length) return `${editing.value.name} is empty.`
  return `${editing.value.name} still holds ${parts.join(' and ')}.`
})

const nameMatches = computed(() => (
  deleteConfirmation.value.trim().toLowerCase() === (editing.value?.name || '').trim().toLowerCase()
))

const destroy = async () => {
  if (!nameMatches.value || deleting.value || deleteBlockedReason.value) return
  deleting.value = true
  deleteError.value = ''
  deleteBlocking.value = []
  const target = editing.value
  try {
    await store.dispatch('Projects/deleteProject', target.id)
    track(EVENTS.PROJECT_DELETED, { project_count: projects.value.length })
    notify({ group: 'success', text: `${target.name} was deleted.` })
    backToList()
  } catch (error) {
    // A 409 names exactly what is blocking ("BBC" still has 2 live streams…)
    // and, when the server is recent enough, itemises it in `data.blocking`.
    // Both go inline: the sentence leads, the list makes it scannable.
    deleteError.value = error?.message
      || `Couldn't delete ${target.name}. If it still contains streams or videos, remove them first and try again.`
    deleteBlocking.value = Array.isArray(error?.data?.blocking)
      ? error.data.blocking.filter((item) => item && item.label)
      : []
  } finally {
    deleting.value = false
  }
}

const requestCreate = () => {
  isOpen.value = false
  emit('create')
}

const requestSwitch = (project) => {
  isOpen.value = false
  emit('switch', project)
}

watch(isOpen, (open) => {
  if (open) loadUsage()
  else backToList()
})

const title = computed(() => (editing.value ? 'Edit workspace' : 'Workspaces'))
</script>

<template>
  <ModalBox v-model="isOpen" :title="title" size="sm" scroll-body>
    <!-- List view ------------------------------------------------------------ -->
    <div v-if="!editing" class="px-4 sm:px-6 pb-2">
      <p class="text-tiny text-surface-8 mb-4">
        {{ allowanceLabel }}. Each workspace keeps its own content. Your team and your plan's limits
        are shared across all of them, and access is set per workspace.
      </p>

      <ul class="flex flex-col gap-2 pl-0 list-none mb-0">
        <li
          v-for="project in projects"
          :key="project.id"
          class="flex items-center gap-3 rounded-lg bg-surface-1 px-3 py-2.5"
        >
          <ProjectTile :name="project.name" :color="project.color" size="size-8" />
          <span class="min-w-0 flex-1">
            <span class="flex items-center gap-2 min-w-0">
              <span class="truncate text-sm font-medium text-white">{{ project.name }}</span>
              <span
                v-if="project.isDefault"
                class="shrink-0 rounded-full bg-surface-3 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-surface-7"
                title="The default workspace. It holds the account's original content and cannot be deleted."
              >
                Default
              </span>
              <span
                v-if="isCurrent(project)"
                class="shrink-0 rounded-full bg-main-transparent px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-main-light"
              >
                Current
              </span>
            </span>
            <span v-if="contentsOf(project)" class="block truncate text-[11px] text-surface-6 mt-0.5">
              {{ contentsOf(project) }}
            </span>
          </span>
          <FormButton
            v-if="!isCurrent(project)"
            type="link"
            size="sm"
            label="Open"
            class="shrink-0 !text-surface-8 hover:!text-white"
            @click="requestSwitch(project)"
          />
          <FormButton type="secondary" size="sm" label="Edit" class="shrink-0" @click="openEditor(project)" />
        </li>
      </ul>
    </div>

    <!-- Editor view ---------------------------------------------------------- -->
    <div v-else class="px-4 sm:px-6 pb-2">
      <FormButton
        type="link"
        size="sm"
        class="-ml-2 mb-3 !text-surface-8 hover:!text-white gap-1"
        aria-label="Back to all workspaces"
        @click="backToList"
      >
        <icon-base><icon-chevron-left /></icon-base>
        All workspaces
      </FormButton>

      <ProjectForm
        ref="form"
        v-model="draft"
        :exclude-id="editing.id"
        :disabled="saving || deleting"
        :is-error="!!saveError"
        @submit="save"
      />
      <Alert v-if="saveError" type="error" class="mt-4" :text="saveError" />

      <!-- The default workspace can never be deleted, so a danger zone with a
           disabled button would be dead UI. One quiet line says why instead. -->
      <p
        v-if="editing.isDefault"
        class="text-[11px] text-surface-6 mt-4 mb-0"
        data-testid="project-default-note"
      >
        This is your default workspace. It holds the account's original content and can't be deleted.
      </p>

      <section v-else class="mt-6 rounded-lg bg-error-transparent p-4">
        <h3 class="text-tiny font-semibold uppercase tracking-wider text-error-light mb-1">Delete workspace</h3>

        <template v-if="!confirmingDelete">
          <p class="text-tiny text-surface-8 mb-3">
            A workspace can only be deleted once it is empty: move or delete its streams, videos,
            playlists and destinations first. Team members whose only workspace this is are removed
            from the team, which frees their seats.
            <span v-if="contentsWarning" class="text-surface-text-1">{{ contentsWarning }}</span>
          </p>
          <FormButton
            type="danger"
            size="sm"
            label="Delete workspace…"
            :disabled="!!deleteBlockedReason"
            @click="confirmingDelete = true"
          />
          <p v-if="deleteBlockedReason" class="text-[11px] text-surface-6 mt-2 mb-0">{{ deleteBlockedReason }}</p>
        </template>

        <template v-else>
          <label for="project-delete-confirmation" class="block text-tiny text-surface-8 mb-3">
            Type <span class="font-semibold text-white">{{ editing.name }}</span> to confirm. This can't be undone.
          </label>
          <FormInput
            v-model="deleteConfirmation"
            id="project-delete-confirmation"
            :placeholder="editing.name"
            :disabled="deleting"
            @keydown.enter.prevent="destroy"
          />
          <Alert v-if="deleteError" type="error" class="mt-3 !items-start" data-testid="project-delete-error">
            <div class="min-w-0">
              <p class="mb-0">{{ deleteError }}</p>
              <ul
                v-if="deleteBlocking.length"
                class="mt-2 mb-0 flex flex-wrap gap-1.5 pl-0 list-none"
                data-testid="project-delete-blocking"
                aria-label="Still in this workspace"
              >
                <li
                  v-for="item in deleteBlocking"
                  :key="item.key || item.label"
                  class="inline-flex items-center rounded-full bg-white/15 px-2 py-0.5 text-[11px] font-medium tabular-nums"
                >
                  {{ item.label }}
                </li>
              </ul>
            </div>
          </Alert>
          <div class="flex items-center gap-2 mt-3">
            <FormButton
              type="red"
              size="sm"
              label="Delete permanently"
              :disabled="!nameMatches || deleting"
              :is-loading="deleting"
              @click="destroy"
            />
            <FormButton type="link" size="sm" label="Keep workspace" :disabled="deleting" @click="resetDeleteState" />
          </div>
        </template>
      </section>
    </div>

    <template #footer>
      <div v-if="!editing" class="flex items-center justify-between gap-2 px-4 sm:px-6 py-4 border-t border-surface-3">
        <Tooltip :active="!canCreate" content="You've reached your plan's workspace limit." placement="top" wrap-trigger wrapper-class="inline-flex">
          <FormButton type="secondary" size="md" class="gap-1.5" :disabled="!canCreate" @click="requestCreate">
            <icon-base><icon-plus /></icon-base>
            New workspace
          </FormButton>
        </Tooltip>
        <FormButton type="main" size="md" label="Done" @click="isOpen = false" />
      </div>
      <div v-else class="flex items-center justify-end gap-2 px-4 sm:px-6 py-4 border-t border-surface-3">
        <FormButton type="secondary" size="md" label="Cancel" :disabled="saving || deleting" @click="backToList" />
        <FormButton type="main" size="md" label="Save changes" :disabled="!canSave" :is-loading="saving" @click="save" />
      </div>
    </template>
  </ModalBox>
</template>
